/**
 * Схемы данных баланса: `packages/engine/data/*.json` (их наполняет E2, формулы экономики — E5).
 * Одни и те же данные читают клиент (бой, цены во вкладках) и сервер (пересчёт журнала, покупки).
 *
 * Правила:
 * - числа целые: время — тики, длины — фиксированная точка, доли — промилле (см. common.ts);
 * - у набора данных есть {@link BalanceData.version}; она попадает в журнал волны, сервер держит предыдущую версию
 *   на время выката (MADR 0002). Любая правка чисел — новая версия;
 * - тексты — ключи i18n, не строки.
 */
import { BalanceVersion } from "./core";
import { BetaDef } from "./beta";
import { BoostDef, HeroDef, SkillDef, TechDef } from "./heroes";
import { BossDef, WaveGenDef, ZombieDef } from "./enemies";
import { DailyTasksDef, FriendRewardDef, ParcelsDef, StreakDef } from "./retention";
import { EconomyDef, HqDef } from "./economy";
import { LeagueDef, SeasonDef } from "./season";
import { z } from "zod";

export * from "./beta";
export * from "./core";
export * from "./economy";
export * from "./enemies";
export * from "./heroes";
export * from "./retention";
export * from "./season";

/** Полный набор данных баланса. Ссылки между разделами (skillId, bossOrder) проверяет {@link BalanceData}. */
export const BalanceData = z
  .object({
    version: BalanceVersion,
    /** 5 героев v1; в бете ещё 3 премиум. */
    heroes: z.array(HeroDef).min(5).max(9),
    skills: z.array(SkillDef),
    boosts: z.array(BoostDef).min(3),
    zombies: z.array(ZombieDef).length(4),
    bosses: z.array(BossDef).min(1),
    tech: z.array(TechDef),
    hq: HqDef,
    waves: WaveGenDef,
    streak: StreakDef,
    economy: EconomyDef,
    dailyTasks: DailyTasksDef.optional(),
    season: SeasonDef.optional(),
    league: LeagueDef.optional(),
    friendReward: FriendRewardDef.optional(),
    parcels: ParcelsDef.optional(),
    /** Только бета. */
    beta: BetaDef.optional(),
  })
  .superRefine((d, ctx) => {
    const skills = new Set(d.skills.map((s) => s.id));
    for (const h of d.heroes) {
      if (!skills.has(h.skillId)) ctx.addIssue({ code: "custom", message: `hero ${h.id}: unknown skill ${h.skillId}` });
    }
    const parcelBoosts = new Set(d.parcels?.boosts.map((b) => b.id));
    for (const r of d.parcels?.rewards ?? []) {
      if (r.type === "boost" && !parcelBoosts.has(r.boostId)) {
        ctx.addIssue({ code: "custom", message: `parcels: unknown boost ${r.boostId}` });
      }
    }
    const bosses = new Set(d.bosses.map((b) => b.id));
    for (const b of d.waves.bossOrder) {
      if (!bosses.has(b)) ctx.addIssue({ code: "custom", message: `waves.bossOrder: unknown boss ${b}` });
    }
    const dup = (ids: string[], what: string) => {
      const seen = new Set<string>();
      for (const id of ids) {
        if (seen.has(id)) ctx.addIssue({ code: "custom", message: `${what}: duplicate id ${id}` });
        seen.add(id);
      }
    };
    dup(
      d.heroes.map((h) => h.id),
      "heroes",
    );
    dup(
      d.skills.map((s) => s.id),
      "skills",
    );
    dup(
      d.boosts.map((b) => b.id),
      "boosts",
    );
    dup(
      d.zombies.map((z) => z.kind),
      "zombies",
    );
    dup(
      d.bosses.map((b) => b.id),
      "bosses",
    );
    dup(
      d.tech.map((t) => t.id),
      "tech",
    );
  });
export type BalanceData = z.infer<typeof BalanceData>;
